import React, { useState } from 'react';
import { Search, Filter, Plus, FileText } from 'lucide-react';
interface QuotesTabProps {
  onSelectQuote: (id: string) => void;
  quotes: any[];
}
export function QuotesTab({
  onSelectQuote,
  quotes
}: QuotesTabProps) {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const statuses = ['All', 'Draft', 'Approved', 'Hold Placed', 'Contract Issued', 'Fully Booked', 'Lost'];
  const filteredQuotes = quotes.filter(q => {
    const matchesStatus = filter === 'All' || q.status === filter;
    const matchesSearch = q.groupName.toLowerCase().includes(search.toLowerCase()) || q.agency.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Fully Booked':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200 font-bold';
      case 'Contract Issued':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Deposit Pending':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Hold Placed':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Approved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Viewed':
        return 'bg-sky-100 text-sky-700 border-sky-200';
      case 'Draft':
      case 'Submitted':
      case 'Pending Review':
        return 'bg-stone-100 text-stone-700 border-stone-200';
      case 'Rejected':
      case 'Lost':
      case 'Expired':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };
  return <div className="space-y-8 animate-in fade-in duration-500 h-full flex flex-col" data-id="element-1051">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1052">
        <div data-id="element-1053">
          <h1 className="font-serif text-3xl text-stone-900 mb-2" data-id="element-1054">Quotes</h1>
          <p className="text-stone-500" data-id="element-1055">
            Manage all group quotes across your portfolio.
          </p>
        </div>
        <button className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 text-sm font-medium transition-colors shadow-sm" data-id="element-1056">
          <Plus className="w-4 h-4" data-id="element-1057" />
          New Quote
        </button>
      </div>

      <div className="bg-white border border-stone-200 shadow-sm flex-1 flex flex-col min-h-0" data-id="element-1058">
        {/* Toolbar */}
        <div className="p-4 border-b border-stone-200 flex flex-col sm:flex-row gap-4 justify-between items-center bg-stone-50/50" data-id="element-1059">
          <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0" data-id="element-1060">
            {statuses.map(s => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${filter === s ? 'bg-stone-200 text-stone-900' : 'text-stone-600 hover:bg-stone-100'}`} data-id="element-1061">
                {s}
              </button>)}
          </div>

          <div className="relative w-full sm:w-64" data-id="element-1062">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1063" />
            <input type="text" placeholder="Search groups or agencies..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 transition-all" data-id="element-1064" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1" data-id="element-1065">
          <table className="w-full text-left text-sm" data-id="element-1066">
            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10" data-id="element-1067">
              <tr data-id="element-1068">
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-1069">
                  Quote ID
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-1070">
                  Group / Agency
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-1071">
                  Property
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-1072">
                  Dates & Rooms
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-1073">
                  Value
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-1074">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-1075">
              {filteredQuotes.length > 0 ? filteredQuotes.map(quote => <tr key={quote.id} onClick={() => onSelectQuote(quote.id)} className="hover:bg-stone-50 cursor-pointer transition-colors group" data-id="element-1076">
                    <td className="p-4 font-mono text-stone-500 group-hover:text-red-600 transition-colors" data-id="element-1077">
                      {quote.id}
                    </td>
                    <td className="p-4" data-id="element-1078">
                      <p className="font-medium text-stone-900" data-id="element-1079">
                        {quote.groupName}
                      </p>
                      <p className="text-stone-500" data-id="element-1080">{quote.agency}</p>
                    </td>
                    <td className="p-4 text-stone-600" data-id="element-1081">{quote.property}</td>
                    <td className="p-4" data-id="element-1082">
                      <p className="text-stone-900" data-id="element-1083">{quote.dates}</p>
                      <p className="text-stone-500" data-id="element-1084">{quote.rooms} rooms</p>
                    </td>
                    <td className="p-4 font-mono text-stone-900" data-id="element-1085">
                      ${quote.value.toLocaleString()}
                    </td>
                    <td className="p-4" data-id="element-1086">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(quote.status)}`} data-id="element-1087">
                        {quote.status}
                      </span>
                    </td>
                  </tr>) : <tr data-id="element-1088">
                  <td colSpan={6} className="p-12 text-center text-stone-500" data-id="element-1089">
                    <FileText className="w-8 h-8 mx-auto mb-3 text-stone-300" data-id="element-1090" />
                    <p data-id="element-1091">No quotes found matching your filters.</p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}